import { 
  collection, 
  addDoc, 
  doc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  updateDoc,
  deleteDoc,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import { db } from './config';

// Message Types
export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderHandle: string;
  senderImageUrl?: string;
  content: string;
  timestamp: Date;
  read: boolean;
  messageType: 'text' | 'image' | 'file';
  replyTo?: string; // ID of message being replied to
}

export interface Conversation {
  id: string;
  participants: string[];
  participantNames: { [userId: string]: string };
  participantHandles: { [userId: string]: string };
  participantImages: { [userId: string]: string };
  lastMessage?: string;
  lastMessageTime?: Date;
  lastMessageSender?: string;
  unreadCount: { [userId: string]: number };
  createdAt: Date;
  updatedAt: Date;
}

export interface TypingIndicator {
  userId: string;
  userName: string;
  conversationId: string;
  isTyping: boolean;
  timestamp: Date;
}

// Message Functions

// Find an existing conversation between two users
export const findConversation = async (userId1: string, userId2: string): Promise<Conversation | null> => {
  try {
    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', userId1)
    );
    
    const snapshot = await getDocs(q);
    
    for (const doc of snapshot.docs) {
      const data = doc.data();
      if (data.participants.includes(userId2) && data.participants.length === 2) {
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
          lastMessageTime: data.lastMessageTime?.toDate?.() || new Date()
        } as Conversation;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error finding conversation:', error);
    return null;
  }
};

// Find or create a conversation between two users
export const findOrCreateConversation = async (
  currentUserId: string,
  currentUserData: { name: string; handle: string; imageUrl?: string },
  targetUserId: string,
  targetUserData: { name: string; handle: string; imageUrl?: string }
): Promise<string> => {
  // First, try to find an existing conversation
  const existingConversation = await findConversation(currentUserId, targetUserId);
  
  if (existingConversation) {
    return existingConversation.id;
  }
  
  // If no existing conversation, create a new one
  const participantData = {
    [currentUserId]: currentUserData,
    [targetUserId]: targetUserData
  };
  
  return createConversation([currentUserId, targetUserId], participantData);
};

export const createConversation = async (
  participants: string[],
  participantData: { [userId: string]: { name: string; handle: string; imageUrl?: string } }
): Promise<string> => {
  const conversation: Omit<Conversation, 'id' | 'createdAt' | 'updatedAt'> = {
    participants,
    participantNames: Object.fromEntries(
      Object.entries(participantData).map(([userId, data]) => [userId, data.name])
    ),
    participantHandles: Object.fromEntries(
      Object.entries(participantData).map(([userId, data]) => [userId, data.handle])
    ),
    participantImages: Object.fromEntries(
      Object.entries(participantData).map(([userId, data]) => [userId, data.imageUrl || ''])
    ),
    unreadCount: Object.fromEntries(participants.map(userId => [userId, 0])),
    lastMessage: '',
    lastMessageTime: new Date(),
    lastMessageSender: ''
  };

  const docRef = await addDoc(collection(db, 'conversations'), {
    ...conversation,
    createdAt: new Date(),
    updatedAt: new Date()
  });

  return docRef.id;
};

export const sendMessage = async (
  conversationId: string,
  senderId: string,
  senderName: string,
  senderHandle: string,
  senderImageUrl: string | undefined,
  content: string,
  messageType: 'text' | 'image' | 'file' = 'text',
  replyTo?: string
): Promise<string> => {
  const message: Omit<Message, 'id' | 'timestamp'> = {
    conversationId,
    senderId,
    senderName,
    senderHandle,
    senderImageUrl,
    content,
    read: false,
    messageType,
    ...(replyTo && { replyTo }) // Only include replyTo if it's defined
  };

  const docRef = await addDoc(collection(db, 'messages'), {
    ...message,
    timestamp: serverTimestamp()
  });

  // Update conversation with last message
  await updateDoc(doc(db, 'conversations', conversationId), {
    lastMessage: content,
    lastMessageTime: serverTimestamp(),
    lastMessageSender: senderId,
    updatedAt: serverTimestamp()
  });

  return docRef.id;
};

export const markMessageAsRead = async (messageId: string): Promise<void> => {
  await updateDoc(doc(db, 'messages', messageId), {
    read: true
  });
};

export const markConversationAsRead = async (
  conversationId: string, 
  userId: string
): Promise<void> => {
  await updateDoc(doc(db, 'conversations', conversationId), {
    [`unreadCount.${userId}`]: 0
  });
};

// Real-time message listeners
export const subscribeToMessages = (
  conversationId: string,
  callback: (messages: Message[]) => void
) => {
  const q = query(
    collection(db, 'messages'),
    where('conversationId', '==', conversationId),
    orderBy('timestamp', 'asc')
  );

  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate() || new Date()
    } as Message));
    callback(messages);
  });
};

export const subscribeToConversations = (
  userId: string,
  callback: (conversations: Conversation[]) => void
) => {
  const q = query(
    collection(db, 'conversations'),
    where('participants', 'array-contains', userId),
    orderBy('lastMessageTime', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const conversations = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      lastMessageTime: doc.data().lastMessageTime?.toDate(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    } as Conversation));
    callback(conversations);
  });
};

// Typing Indicators
export const setTypingIndicator = async (
  userId: string,
  userName: string,
  conversationId: string,
  isTyping: boolean
): Promise<void> => {
  await setDoc(doc(db, 'typingIndicators', `${conversationId}_${userId}`), {
    userId,
    userName,
    conversationId,
    isTyping,
    timestamp: serverTimestamp()
  });
};

export const subscribeToTypingIndicators = (
  conversationId: string,
  callback: (typingUsers: TypingIndicator[]) => void
) => {
  const q = query(
    collection(db, 'typingIndicators'),
    where('conversationId', '==', conversationId),
    where('isTyping', '==', true)
  );

  return onSnapshot(q, (snapshot) => {
    const typingUsers = snapshot.docs.map(doc => ({
      userId: doc.data().userId,
      userName: doc.data().userName,
      conversationId: doc.data().conversationId,
      isTyping: doc.data().isTyping,
      timestamp: doc.data().timestamp?.toDate() || new Date()
    } as TypingIndicator));
    callback(typingUsers);
  });
};

// Clean up old typing indicators
export const cleanupTypingIndicators = async (): Promise<void> => {
  const cutoffTime = new Date(Date.now() - 10000); // 10 seconds ago
  
  const q = query(
    collection(db, 'typingIndicators'),
    where('timestamp', '<', cutoffTime)
  );
  
  const snapshot = await getDocs(q);
  const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
  await Promise.all(deletePromises);
};
